import COPY from "../content/siteCopy.json";
import React, { useEffect, useState } from "react";
import { CalendlyLink, SmartLink } from "../SiteHeader";
import { maxPrice, money, qFromSnap } from "./math";

const STEPS = [
  { key: "income", label: "Household income", hint: "Annual, before tax. Everyone who'll be on the application.", sass: "Round numbers are fine. We're not the CRA.", chips: [60000, 85000, 110000, 150000], suffix: "/yr" },
  { key: "down", label: "Down payment saved", hint: "Savings, FHSA, RRSP, a gift from family—all of it.", sass: "A gift from mom counts. A lottery ticket doesn't. Yet.", chips: [15000, 25000, 50000, 100000] },
  { key: "debts", label: "Monthly debt payments", hint: "Car, cards, student loans, lines of credit—what goes out every month.", sass: "Truck payment. Say it with us.", chips: [0, 300, 600, 1000], suffix: "/mo" },
];

const TABLE = [
  ["Write an offer a seller takes seriously", [false, "They can smell it from the driveway"], [true, "Verified. In writing."]],
  ["Hold today's rate for 120 days", [false, "Websites don't hold rates"], [true, "Free, and automatic"]],
  ["Know a lender actually read your file", [false, "Nobody read anything"], [true, "Credit, income, down payment—checked"]],
  ["Survive a five-day financing condition", [false, "Coin flip"], [true, "Usually with days to spare"]],
  ["Tell your mom you're \"basically approved\"", [true, "She'll believe you"], [true, "And this time it's true"]],
];

const fmt = (n) => (n === "" || n == null ? "" : Number(n).toLocaleString("en-CA"));
const parse = (s) => { const n = Number(String(s).replace(/[^0-9]/g, "")); return Number.isFinite(n) ? n : 0; };

export function PrequalWizard({ city, snap }) {
  const [vals, setVals] = useState({ income: "", down: "", debts: "" });
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const cur = STEPS[step];
  const set = (v) => setVals((p) => ({ ...p, [cur.key]: v }));
  const filled = (k) => vals[k] !== "" && (k === "debts" || Number(vals[k]) > 0);
  const canNext = filled(cur.key);
  const next = () => { if (!canNext) return; if (step < 2) setStep(step + 1); else setDone(true); };
  useEffect(() => { if (step > 0) document.getElementById(`pq-${STEPS[step].key}`)?.focus({ preventScroll: true }); }, [step]);
  const reset = () => { setVals({ income: "", down: "", debts: "" }); setStep(0); setDone(false); };
  const q = qFromSnap(snap);
  const result = done ? maxPrice({ income: Number(vals.income), down: Number(vals.down), debts: Number(vals.debts), q }) : null;
  const place = city || "Alberta";

  return (
    <section className="pq" data-testid="prequal-wizard">
      <div className="wrap">
        <div className="pq-head">
          <div className="eyebrow">The 90-second pre-qualification · totally free · means almost nothing</div>
          <h2 className="display pq-h2">Get pre-qualified right here. <em>Then find out why it doesn't count.</em></h2>
          <p className="pq-lead">{COPY.solution.extras["pre-approval"][0].text}</p>
        </div>
        <div className="pq-card">
          {!done ? (
            <>
              <div className="pq-boxes" role="tablist">
                {STEPS.map((s, i) => (
                  <button type="button" key={s.key} role="tab" aria-selected={i === step} className={`pq-box${i === step ? " active" : ""}${filled(s.key) ? " filled" : ""}`} onClick={() => i < step || filled(s.key) ? setStep(i) : null} data-testid={`prequal-box-${s.key}`}>
                    {i === step && <img src="/brand/solution-preapproval.png" alt="" className="pq-hopper" aria-hidden="true" />}
                    <span className="pq-box-n">{i + 1}</span>
                    <span className="pq-box-label">{s.label}</span>
                    <strong className="pq-box-val">{filled(s.key) ? `$${fmt(vals[s.key])}${s.suffix || ""}` : "—"}</strong>
                  </button>
                ))}
              </div>
              <div className="pq-step" key={cur.key}>
                <label className="pq-label" htmlFor={`pq-${cur.key}`}>{cur.label}<small>{cur.hint}</small></label>
                <div className="pq-input"><span>$</span><input id={`pq-${cur.key}`} inputMode="numeric" autoComplete="off" placeholder="0" value={fmt(vals[cur.key])} onChange={(e) => set(parse(e.target.value))} onKeyDown={(e) => e.key === "Enter" && next()} data-testid={`prequal-input-${cur.key}`} />{cur.suffix && <em>{cur.suffix}</em>}</div>
                <div className="pq-chips">{cur.chips.map((c) => <button type="button" key={c} className={`pq-chip${Number(vals[cur.key]) === c && vals[cur.key] !== "" ? " on" : ""}`} onClick={() => set(c)} data-testid={`prequal-chip-${cur.key}-${c}`}>{c === 0 ? "None" : `$${fmt(c)}`}</button>)}</div>
                <div className="pq-foot">
                  <span className="pq-sass">{cur.sass}</span>
                  <button type="button" className="btn" disabled={!canNext} onClick={next} data-testid="prequal-next">{step < 2 ? "Next box →" : "Pre-qualify me"}</button>
                </div>
              </div>
            </>
          ) : (
            <div className="pq-result" data-testid="prequal-result">
              <div className="pq-confetti" aria-hidden="true">{Array.from({ length: 14 }).map((_, i) => <i key={i} style={{ "--i": i }} />)}</div>
              {result ? (
                <>
                  <div className="eyebrow">Ta-da.</div>
                  <h3 className="display pq-tada">You're pre-qualified for about <span data-testid="prequal-amount">{money(result.price)}</span>.</h3>
                  <p className="pq-sub">Roughly {money(result.payment)} a month for the mortgage in {place}, stress-tested at {q.qualifyingRate}%. Which means…</p>
                </>
              ) : (
                <>
                  <div className="eyebrow">Ta-da…</div>
                  <h3 className="display pq-tada">The math says <span>not quite yet.</span></h3>
                  <p className="pq-sub">Usually the down payment or the debt load is the bottleneck—and both have fixes. Which brings us to the point…</p>
                </>
              )}
              <div className="pq-nothing"><b>Nothing.</b> It means nothing. A website just did math on numbers you typed. Nobody checked your credit, your income or where the down payment came from. Sellers know this. Realtors know this. Now you know this.</div>
              <div className="pq-table" role="table" data-testid="prequal-table">
                <div className="pq-tr pq-th" role="row"><span>What can I actually do with it?</span><span>Pre-qualified</span><span>Pre-approved</span></div>
                {TABLE.map(([what, pq, pa]) => (
                  <div className="pq-tr" role="row" key={what}>
                    <span>{what}</span>
                    <span className={pq[0] ? "yes" : "no"} data-col="Pre-qualified"><b>{pq[0] ? "✓" : "✗"}</b> {pq[1]}</span>
                    <span className={pa[0] ? "yes" : "no"} data-col="Pre-approved"><b>{pa[0] ? "✓" : "✗"}</b> {pa[1]}</span>
                  </div>
                ))}
              </div>
              <div className="pq-cta">
                <div><b>Want the number that actually counts?</b><p>A real pre-approval: a human reads your file, a lender puts it in writing, your rate's held for 120 days. Free, 1–3 business days.</p></div>
                <div className="pq-cta-btns"><CalendlyLink className="btn" testId="prequal-cta">Find my actual number</CalendlyLink><button type="button" className="textlink pq-again" onClick={reset} data-testid="prequal-again">Try different numbers</button></div>
              </div>
              {!result && <p className="micro">Short on down payment? <SmartLink href="/calculators/budget-boost/">Budget Boost</SmartLink> shows the moves that get you there faster.</p>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
